import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Stack,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";
import { doc, setDoc } from "firebase/firestore";
import api from "../../services/api";

const logos = {
  quickbooks:
    "https://upload.wikimedia.org/wikipedia/commons/7/7e/Intuit_QuickBooks_logo.svg",
  xero: "https://upload.wikimedia.org/wikipedia/commons/7/70/Xero_software_logo.svg",
  brightbooks: "https://via.placeholder.com/40?text=B",
};

export default function AccountantOnboarding() {
  const navigate = useNavigate();

  const [businessName, setBusinessName] = useState("");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isTrial, setIsTrial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", type: "" });

  const handleProviderSelect = (provider) => {
    if (provider === "xero" || provider === "brightbooks") {
      setSnack({
        open: true,
        message: `${
          provider.charAt(0).toUpperCase() + provider.slice(1)
        } is coming soon!`,
        type: "info",
      });
      return;
    }
    setSelectedProvider(provider);
  };

  const handleTrial = (trial) => {
    setIsTrial(trial);
  };

  const handleComplete = async () => {
    if (loading) return;
    if (!selectedProvider) {
      setSnack({
        open: true,
        message: "Please select a provider.",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      // Save business name + trial info
      await setDoc(
        doc(db, "users", auth.currentUser.uid),
        {
          businessName: businessName || null,
          trial: isTrial,
          onboardingCompleted: true,
        },
        { merge: true }
      );

      // Save provider
      await api.post("/accountant/provider", { provider: selectedProvider });

      if (selectedProvider === "quickbooks") {
        const token = await auth.currentUser.getIdToken();
        window.location.href = `http://localhost:8080/api/quickbooks/auth?token=${token}`;
        return; // QuickBooks will redirect after auth
      }

      // If no provider auth needed, go to dashboard
      navigate("/accountant");
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: "Setup failed. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={4} maxWidth="sm" mx="auto">
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Welcome to SortlyBooks!
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          Let’s get your accountant account set up.
        </Typography>

        <TextField
          fullWidth
          label="Business Name (optional)"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Typography variant="h6" gutterBottom>
          Select Your Accounting Provider
        </Typography>

        <Stack spacing={2} mt={2} mb={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: selectedProvider === "quickbooks" ? 6 : 1,
            }}
          >
            <CardActionArea onClick={() => handleProviderSelect("quickbooks")}>
              <CardContent
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <img
                  src={logos.quickbooks}
                  alt="QuickBooks"
                  style={{ width: 40, height: 40 }}
                />
                <Box>
                  <Typography fontWeight={600}>QuickBooks</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connect to QuickBooks for expense and invoice syncing.
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>

          <Card sx={{ borderRadius: 2, opacity: 0.6 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={logos.xero}
                alt="Xero"
                style={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography fontWeight={600} color="text.secondary">
                  Xero (Coming Soon)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect to Xero for expense and invoice syncing.
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, opacity: 0.6 }}>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={logos.brightbooks}
                alt="BrightBooks"
                style={{ width: 40, height: 40 }}
              />
              <Box>
                <Typography fontWeight={600} color="text.secondary">
                  BrightBooks (Coming Soon)
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction="row" spacing={2} mb={3}>
          <Button
            variant={isTrial ? "contained" : "outlined"}
            onClick={() => handleTrial(true)}
          >
            Start Free Trial
          </Button>
          <Button
            variant={isTrial === false ? "contained" : "outlined"}
            onClick={() => handleTrial(false)}
          >
            Skip Trial
          </Button>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          disabled={loading}
          onClick={handleComplete}
          sx={{
            backgroundColor: "#1e40af",
            "&:hover": { backgroundColor: "#1e3a8a" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Complete Setup"
          )}
        </Button>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert
          severity={snack.type}
          variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
